import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import AssignKeysView from "./assign-bms-keys/AssignKeysView";
import LicenseKeysTable from "./LicenseKeysTable";
import ExportIcon from "components/icons/ExportIcon";
import ApplicationLayout from "components/layout/ApplicationLayout";
import { LICENSES_ROUTE } from "components/router/Routes";
import { AUTH_LICENSE_ASSIGN, AUTH_LICENSE_KEY_MANAGEMENT } from "domain/authority";
import { LicenseKeyFilter } from "domain/licenseKeys";
import { LicenseData } from "domain/licenses";
import { hasAnyBmsLicenses } from "services/licenses/licenseRepository";
import { licenseService } from "services/licenses/LicenseService";
import { getStanUrl } from "services/login/endpointRepository";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import {
    setHasBmsAllLicenses,
    setHasBmsBbtiLicenses,
    setHasBmsInsuranceLicenses,
    setHasBmsLeaseLicenses,
    setHasBmsLicenses,
    setHasBmsNtfLicenses,
    setHasBmsValidationLicenses,
    setHasUsdkLicenses,
} from "store/license";
import buttonStyle from "styles/buttons.scss";
import { ParameterQuery } from "utils/commonFunctions";
import { logger } from "utils/logging";
import { useTableRefresh } from "utils/useTableRefresh";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const mapState = (state: StoreState) => ({
    tenantUuid: state.userReducer.user?.tenantUuid,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
});
const connector = connect(mapState, {
    setHasBmsLicenses,
    setHasBmsLeaseLicenses,
    setHasBmsValidationLicenses,
    setHasBmsBbtiLicenses,
    setHasBmsInsuranceLicenses,
    setHasUsdkLicenses,
    setHasBmsNtfLicenses,
    setHasBmsAllLicenses,
});

function BmsLicenseKeysView(props: ConnectedProps<typeof connector>): JSX.Element {
    const { t } = useTranslation();
    const { refreshCount, onHandleRefresh } = useTableRefresh();
    const [allowAssigningKeys, setAllowAssigningKeys] = React.useState<boolean>(false);
    const [availableLicenses, setAvailableLicenses] = React.useState<LicenseData[]>();
    const theme = useTheme();
    const [showExportButton, setShowExportButton] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [filterType, setFilterType] = React.useState<LicenseKeyFilter>(LicenseKeyFilter.ALL);
    const [quickSearch, setQuickSearch] = React.useState<string>();

    React.useEffect(() => {
        const abortController = new AbortController();
        licenseService
            .fetchUserOwnBmsLicense(
                abortController,
                props.tenantDetails.uuid,
                props.tenantDetails.type,
                props.tenantDetails.tenantTier,
                !hasTenantCookie()
            )
            .then((ownLicenses) => {
                if (ownLicenses) {
                    setAllowAssigningKeys(true);
                    setAvailableLicenses(ownLicenses);
                    const hasValidationLicenses = ownLicenses.some((license) => license.type === "bms-validation");
                    props.setHasBmsValidationLicenses(hasValidationLicenses);
                    const hasBbtiLicenses = ownLicenses.some((license) => license.type === "bms-bbti");
                    props.setHasBmsBbtiLicenses(hasBbtiLicenses);
                    const hasInsuranceLicenses = ownLicenses.some((license) => license.type === "bms-insurance");
                    props.setHasBmsInsuranceLicenses(hasInsuranceLicenses);
                    const hasUsdkLicenses = ownLicenses.some((license) => license.type === "usdk");
                    props.setHasUsdkLicenses(hasUsdkLicenses);
                    const hasNtfLicenses = ownLicenses.some((license) => license.type === "bms-ntf");
                    props.setHasBmsNtfLicenses(hasNtfLicenses);
                    const hasLeaseLicenses = ownLicenses.some((license) => license.type === "bms-lease");
                    props.setHasBmsLeaseLicenses(hasLeaseLicenses);
                    const hasAllLicenses = ownLicenses.some((license) => license.type === "bms-all");
                    props.setHasBmsAllLicenses(hasAllLicenses);
                    props.setHasBmsLicenses(hasAnyBmsLicenses());
                }
                setLoading(false);
            })
            .catch(() => {
                logger.error("Exception occurred while fetching available licenses.");
                setLoading(false);
            });
    }, []);

    const exportLicenseKeys = () => {
        usageStatisticsService.sendEvent({
            category: Category.LICENSE,
            action: Action.EXPORT_LICENSE_KEYS,
        });

        window.location.href =
            getStanUrl() +
            "/license-keys/export" +
            new ParameterQuery().add("search", quickSearch).add("filter", filterType).createQueryString();
    };

    const exportButton = showExportButton ? (
        <button
            data-tip={t("LicenseKeys.export.tooltip")}
            onClick={exportLicenseKeys}
            data-testid={testIds.workArea.license.bms.exportKeysButton}
            className={buttonStyle.secondaryButtonWithoutIcon}
        >
            <ExportIcon color={theme.iconFillColor} size={16} />
            {t("LicenseKeys.export.title")}
        </button>
    ) : null;

    const createViewActions = () => {
        const assignButton =
            allowAssigningKeys &&
            availableLicenses &&
            userSessionService.userHasAllAuthorities([AUTH_LICENSE_KEY_MANAGEMENT]) &&
            userSessionService.userHasAllAuthorities([AUTH_LICENSE_ASSIGN]) ? (
                <AssignKeysView updateTable={onHandleRefresh} availableLicenses={availableLicenses} fromBms={true} />
            ) : undefined;
        return (
            <>
                {exportButton}
                {assignButton}
            </>
        );
    };

    return (
        <ApplicationLayout
            viewTitle={t("LicenseKeys.mobileSolutionsKeys")}
            view={
                <LicenseKeysTable
                    count={refreshCount}
                    setExportButtonVisibility={setShowExportButton}
                    availableLicenses={availableLicenses ?? []}
                    loading={loading}
                    filterType={filterType}
                    setFilterType={setFilterType}
                    setQuickSearch={setQuickSearch}
                    quickSearch={quickSearch}
                />
            }
            mainRoute={LICENSES_ROUTE}
            viewActions={createViewActions()}
            enableRefresh
            onHandleRefresh={onHandleRefresh}
        />
    );
}

export default connector(BmsLicenseKeysView);
